import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import GoogleMapReact from "google-map-react";
import RoomIcon from "@mui/icons-material/Room";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

import Chip from "@mui/material/Chip";
import DoneIcon from "@mui/icons-material/Done";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import QuestionIcon from "@mui/icons-material/HelpOutline";

import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import Marker from "shared/components/UIElements/Marker";

import { VALIDATOR_REQUIRE } from "shared/util/validators";

import { ADDRESS_QUALITY } from "@routebot/constants";
import { useBackend } from "shared/hooks/backend-hook";
import { useUsageGuard } from "shared/hooks/useUsageGuard";
import Tooltip from "@mui/material/Tooltip";

import "./Map.css";

const normalizeAddressValue = (value) => (value ? String(value).trim().replace(/\s+/g, " ") : "");
const didAddressChange = (previousValue, nextValue) =>
  normalizeAddressValue(previousValue) !== normalizeAddressValue(nextValue);

const highlightStyleByStage = {
  visible: {
    backgroundColor: "#fff8e1",
    transition: "background-color 0.4s ease-out",
  },
};

const MapSinglePoint = ({
  id,
  title,
  initialAddress,
  initialLocation,
  initialAddressInSync,
  onMapDataChange,
  isRequired = true,
  isOnlyAddress = false,
  initialAddressQuality,
  geofenceRadiusKm,
  mapHeight,
}) => {
  const { t } = useTranslation();
  const { getLocationFromAddress, getAddressFromLocation } = useBackend();
  const { checkUsageForMapsAddressToLocation, checkUsageForMapsLocationToAddress, UsageGuardModal } = useUsageGuard();
  const [openError, setOpenError] = useState(false);

  const [showMap, setShowMap] = useState(true);

  const addressRef = useRef(initialAddress || "");

  const [address, setAddress] = useState(initialAddress || "");
  const [location, setLocation] = useState(initialLocation || null);
  const [mapCenter, setMapCenter] = useState(initialLocation || null);
  const [addressInSync, setAddressInSync] = useState(initialAddressInSync || false);
  const [addressQuality, setAddressQuality] = useState(initialAddressQuality || null);
  const [isMapBusy, setIsMapBusy] = useState(false);
  const [addressHighlightStage, setAddressHighlightStage] = useState("idle");

  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const geofenceCircleRef = useRef(null);

  const clearGeofenceCircle = () => {
    if (geofenceCircleRef.current) {
      geofenceCircleRef.current.setMap(null);
      geofenceCircleRef.current = null;
    }
  };

  const drawGeofenceCircle = () => {
    if (geofenceRadiusKm === undefined || geofenceRadiusKm === null) {
      clearGeofenceCircle();
      return;
    }

    if (!mapRef.current || !mapsRef.current || !location?.lat || !location?.lng) {
      clearGeofenceCircle();
      return;
    }

    const radiusMeters = Number(geofenceRadiusKm) * 1000;
    if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) {
      clearGeofenceCircle();
      return;
    }

    clearGeofenceCircle();
    geofenceCircleRef.current = new mapsRef.current.Circle({
      strokeColor: "#007bff",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#007bff",
      fillOpacity: 0.15,
      map: mapRef.current,
      center: { lat: Number(location.lat), lng: Number(location.lng) },
      radius: radiusMeters,
    });
  };

  const handleGoogleApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    drawGeofenceCircle();
  };

  useEffect(() => {
    drawGeofenceCircle();
  }, [geofenceRadiusKm, location?.lat, location?.lng]);

  useEffect(() => {
    return () => clearGeofenceCircle();
  }, []);

  useEffect(() => {
    if (addressHighlightStage === "visible") {
      const timeoutId = setTimeout(() => setAddressHighlightStage("idle"), 700);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [addressHighlightStage]);
  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenError(false);
  };

  const mapClickHandler = async (event) => {
    if (isMapBusy) {
      return;
    }
    setIsMapBusy(true);

    // Check usage allowance before making API call
    const usageCheck = await checkUsageForMapsLocationToAddress();
    if (!usageCheck.allowed) {
      setIsMapBusy(false);
      return;
    }

    const currentLocation = { lat: event.lat, lng: event.lng };
    let addressInfo = null;
    const previousAddressValue = addressRef.current;
    try {
      addressInfo = await getAddressFromLocation(currentLocation);
    } catch (error) {
      setOpenError(true);
    }
    if (addressInfo) {
      const updatedAddress = addressInfo.address || "";
      const hasChanged = didAddressChange(previousAddressValue, updatedAddress);
      setAddressHighlightStage(hasChanged ? "visible" : "idle");
      addressRef.current = updatedAddress;
      setLocation(addressInfo.location);
      setAddress(updatedAddress);
      onMapDataChange(updatedAddress, addressInfo.location, true, addressInfo.addressQuality);
      setAddressInSync(true);
      setAddressQuality(addressInfo.addressQuality);
    } else {
      setOpenError(true);
      setAddressInSync(false);
      setAddressQuality(null);
      setAddressHighlightStage("idle");
      onMapDataChange(null, null, false, null);
    }
    setIsMapBusy(false);
  };

  const SetLocationFromAddress = async () => {
    if (isMapBusy || !addressRef.current) {
      return;
    }
    setIsMapBusy(true);

    // Check usage allowance before making API call
    const usageCheck = await checkUsageForMapsAddressToLocation();
    if (!usageCheck.allowed) {
      setIsMapBusy(false);
      return;
    }

    const manualAddressValue = addressRef.current || "";
    setAddress(manualAddressValue);
    let currentLocation = null;
    try {
      currentLocation = await getLocationFromAddress(manualAddressValue);
    } catch (error) {
      setOpenError(true);
    }
    if (currentLocation) {
      const updatedAddress = currentLocation?.address || "";
      const hasChanged = didAddressChange(manualAddressValue, updatedAddress);
      setAddressHighlightStage(hasChanged ? "visible" : "idle");
      addressRef.current = updatedAddress;
      setLocation(currentLocation?.location);
      setAddress(updatedAddress);
      setMapCenter(currentLocation?.location);
      setAddressInSync(true);
      setAddressQuality(currentLocation?.addressQuality);
      onMapDataChange(currentLocation?.address, currentLocation?.location, true, currentLocation?.addressQuality);
      setAddressInSync(true);
    } else {
      setOpenError(true);
      setAddressInSync(false);
      setAddressQuality(null);
      setAddressHighlightStage("idle");
      onMapDataChange(null, null, false, null);
    }

    setShowMap(true);
    setIsMapBusy(false);
  };

  const handleKeyUpOnAddress = async (event) => {
    const addressText = addressRef.current;
    if (event.key === "Enter") {
      SetLocationFromAddress();
    } else if (!addressText || addressText !== address) {
      setAddressInSync(false);
      onMapDataChange(addressText, null, false, null);
      if (addressHighlightStage !== "idle") {
        setAddressHighlightStage("idle");
      }
    } else {
      setAddressInSync(true);
      onMapDataChange(addressText, location, true, addressQuality);
    }
  };

  return (
    <React.Fragment>
      <UsageGuardModal />
      <Snackbar
        open={openError}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
          {t("noMatchingLocation")}
        </MuiAlert>
      </Snackbar>

      <Input
        key={`${id}_${address}`}
        id={`${id}_${address}`}
        element="input"
        type="text"
        label={title || t("address") + (isRequired ? " *" : "")}
        validators={[VALIDATOR_REQUIRE()]}
        errorText={t("requireField", { item: t("address") })}
        initialValue={address}
        initialValid={true}
        onInput={(_id, value) => (addressRef.current = value)}
        onKeyUp={handleKeyUpOnAddress}
        dontShowError={!isRequired}
        elementStyle={highlightStyleByStage[addressHighlightStage]}
        description={
          !isOnlyAddress
            ? isRequired
              ? t("singleMapAddressRequiredDescription")
              : t("singleMapAddressNotRequiredDescription")
            : null
        }
      />

      {!isOnlyAddress && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={
                !addressRef.current
                  ? t("enterAddress")
                  : addressInSync
                  ? t("addressAndLocationInSync")
                  : t("addressAndLocationNotInSync")
              }
              color={addressInSync ? "success" : "warning"}
              icon={addressInSync ? <DoneIcon /> : <CancelIcon />}
              size="small"
            />

            {addressQuality === ADDRESS_QUALITY.INCOMPLETE && addressInSync && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "5px",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <Chip label={t("addressIncomplete")} color="warning" icon={<WarningIcon />} size="small" />

                <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{t("addressIncompleteDescription")}</span>}>
                  <QuestionIcon sx={{ color: "gray", width: "20px", height: "auto" }} />
                </Tooltip>
              </div>
            )}
          </div>

          <Button type="button" size="small" danger onClick={SetLocationFromAddress}>
            {t("getLocationFromAddress")}
          </Button>
        </div>
      )}

      {showMap && location ? (
        <div
          className="single-point-map-container"
          style={mapHeight ? { height: mapHeight } : undefined}
          aria-busy={isMapBusy}
        >
          <GoogleMapReact
            options={function (maps) {
              return {
                mapTypeControl: true,
                mapTypeId: "roadmap",
                mapTypeControlOptions: {
                  mapTypeIds: ["satellite", "roadmap"],
                  style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
                  position: maps.ControlPosition.TOP_LEFT,
                },
                fullscreenControl: false,
                styles: [
                  {
                    featureType: "poi",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "poi.business",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "poi.park",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "poi.medical",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "poi.school",
                    stylers: [{ visibility: "on" }],
                  },
                  {
                    featureType: "road",
                    elementType: "labels",
                    stylers: [{ visibility: "on" }],
                  },
                ],
              };
            }}
            center={{ lat: Number(mapCenter.lat.toFixed(6)), lng: Number(mapCenter.lng.toFixed(6)) }}
            defaultZoom={16}
            onClick={mapClickHandler}
            onGoogleApiLoaded={
              geofenceRadiusKm !== undefined && geofenceRadiusKm !== null ? handleGoogleApiLoaded : undefined
            }
          >
            {location && <Marker Icon={RoomIcon} lat={location.lat} lng={location.lng} error={!addressInSync} />}
          </GoogleMapReact>
          {isMapBusy && (
            <div className="single-point-map-overlay" role="status" aria-live="polite">
              <CircularProgress size={32} />
              <span>{t("Loading")}</span>
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: "20px" }}></div>
      )}
    </React.Fragment>
  );
};

export default MapSinglePoint;
